import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowUpIcon } from 'lucide-react';
import { useState } from 'react';

import {
  ACTIVITY_MESSAGES_QUERY,
  type ActivityMessagesData,
  type ActivityMessagesVars
} from './queries/activityMessages';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';

const SEND_COMMENT = gql`
  mutation AddNote($recordId: ID!, $body: String!) {
    addMessage(recordId: $recordId, body: $body, isInternalNote: true) {
      ok
    }
  }
`;

interface Props {
  id: string;
}

const CommentsTab = ({ id }: Props) => {
  const [sendComment, { loading: isSendingComment }] =
    useMutation(SEND_COMMENT);

  const [comment, setComment] = useState('');

  const {
    data: data1,
    error: error1,
    loading: loading1
  } = useQuery<ActivityMessagesData, ActivityMessagesVars>(
    ACTIVITY_MESSAGES_QUERY,
    { variables: { recordId: id } }
  );

  if (loading1) {
    return (
      <div className="grid h-40 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error1) {
    return <div>{'Error'}</div>;
  }

  const commentsWithBody = (data1?.mailMessages ?? []).filter(
    msg => msg.body != null && msg.body.trim() !== ''
  );

  return (
    <>
      <div className="max-h-72 space-y-4 overflow-y-auto pt-3">
        {commentsWithBody.length === 0 ? (
          <div className="text-muted-foreground py-4 text-sm">
            No comments yet.
          </div>
        ) : (
          commentsWithBody.map(comment => (
            <div
              className="border-b pb-4"
              key={comment.id}
            >
              <div className="space-between mb-4 flex items-center justify-between">
                {comment.authorName && (
                  <div className="flex items-center gap-x-2">
                    <div className="grid size-8 place-content-center rounded-full bg-blue-200 text-sm text-blue-700">
                      {comment.authorName.split(' ')[0][0]}
                      {/* comment.authorName.split(' ')[1][0] */}
                    </div>
                    <div className="text-sm font-medium">
                      {comment.authorName}
                    </div>
                  </div>
                )}
                {comment.createDate && (
                  <div className="text-muted-foreground text-sm font-light">
                    {formatDistanceToNow(comment.createDate, {
                      addSuffix: true
                    })}
                  </div>
                )}
              </div>
              <div
                className="text-sm leading-none font-light"
                // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                dangerouslySetInnerHTML={{ __html: comment.body ?? '' }}
              />
            </div>
          ))
        )}
      </div>
      <InputGroup>
        <InputGroupTextarea
          maxLength={280}
          onChange={event => {
            setComment(event.target.value);
          }}
          placeholder="Type your comment..."
          value={comment}
        />
        <InputGroupAddon align="block-end">
          <InputGroupText className="text-muted-foreground text-sm font-medium">
            {comment.length}/280 characters
          </InputGroupText>
          <InputGroupButton
            className="ml-auto"
            disabled={comment === '' || isSendingComment}
            onClick={() => {
              void (async () => {
                try {
                  await sendComment({
                    refetchQueries: [
                      {
                        query: ACTIVITY_MESSAGES_QUERY,
                        variables: { recordId: id }
                      }
                    ],
                    variables: {
                      body: comment,
                      recordId: id
                    }
                  });

                  setComment('');
                } catch (error) {
                  console.error(error);
                }
              })();
            }}
            size="icon-sm"
            variant="default"
          >
            {isSendingComment ? (
              <Spinner />
            ) : (
              <>
                <ArrowUpIcon />
                <span className="sr-only">Send</span>
              </>
            )}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </>
  );
};

export default CommentsTab;
